/**
 * CDM Connectivity — Track 2.8 network connectivity engine.
 *
 * Three modes:
 *   - Paths         the joined cdm_network_paths view, filtered/sorted
 *   - Blast radius  iterative graph walk from a chosen asset
 *   - Shadow paths  observed flows without any matching declared rule
 *
 * Whitespace vs Axonius — they don't do connectivity at the rule × flow
 * level. Confirmed paths are the green path; unused-permission paths are
 * the prune list; shadow paths are the findings.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Network, RefreshCw, ChevronLeft, AlertTriangle, Wifi, Search,
  Activity, Crosshair, Play,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type PathStatus = 'confirmed' | 'unused_permission' | 'shadow_path'

interface NetworkPath {
  id: string
  src_asset_id: string
  dst_asset_id: string
  port: number
  proto: string
  allowed_by: string[]
  allowed_by_count: number
  observed_in_count: number
  last_observed: string | null
  status: PathStatus
  refreshed_at: string | null
}

interface ShadowPath {
  id: string
  src_asset_id: string
  dst_asset_id: string
  port: number
  proto: string
  observed_in_count: number
  last_observed: string | null
}

interface BlastEdge {
  src: string
  dst: string
  port: number
  proto: string
  status: PathStatus
}

interface BlastNode {
  asset_id: string
  shortest_hop: number
  via: BlastEdge[]
}

const STATUS_TONE: Record<PathStatus, string> = {
  confirmed:         'bg-emerald/10 text-emerald border-emerald/30',
  unused_permission: 'bg-blue/10 text-blue-light border-blue/30',
  shadow_path:       'bg-rose/10 text-rose-light border-rose/30',
}

const STATUS_LABEL: Record<PathStatus, string> = {
  confirmed:         'CONFIRMED',
  unused_permission: 'UNUSED-PERM',
  shadow_path:       'SHADOW',
}

type Mode = 'paths' | 'blast' | 'shadow'

function shortId(id: string): string {
  if (!id) return '—'
  return id.length > 8 ? id.slice(0, 8) : id
}

function formatTs(ts: string | null): string {
  if (!ts) return '—'
  try {
    const d = new Date(ts)
    return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
  } catch {
    return ts
  }
}

export default function CdmConnectivityPage() {
  const [mode, setMode] = useState<Mode>('paths')

  // Paths tab state
  const [paths, setPaths] = useState<NetworkPath[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<PathStatus | ''>('')
  const [search, setSearch] = useState('')
  const [portFilter, setPortFilter] = useState('')
  const [protoFilter, setProtoFilter] = useState('')

  // Shadow tab state
  const [shadowRows, setShadowRows] = useState<ShadowPath[]>([])

  // Blast-radius tab state
  const [blastAsset, setBlastAsset] = useState('')
  const [maxHops, setMaxHops] = useState(5)
  const [blastNodes, setBlastNodes] = useState<BlastNode[]>([])
  const [blastLoading, setBlastLoading] = useState(false)
  const [blastError, setBlastError] = useState('')

  const [refreshing, setRefreshing] = useState(false)
  const [message, setMessage] = useState('')

  const loadPaths = async () => {
    setLoading(true)
    const params: Record<string, string | number> = { limit: 1000 }
    if (statusFilter) params.status = statusFilter
    if (portFilter) params.port = Number(portFilter) || 0
    if (protoFilter) params.proto = protoFilter
    try {
      const r = await api.get('/api/cdm/network-paths', { params })
      setPaths((r.data?.paths || []) as NetworkPath[])
    } catch {
      setPaths([])
    } finally {
      setLoading(false)
    }
  }

  const loadShadow = async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/cdm/network-paths/shadow', { params: { limit: 500 } })
      setShadowRows((r.data?.paths || []) as ShadowPath[])
    } catch {
      setShadowRows([])
    } finally {
      setLoading(false)
    }
  }

  const runBlast = async () => {
    if (!blastAsset.trim()) {
      setBlastError('Asset id required')
      return
    }
    setBlastLoading(true)
    setBlastError('')
    try {
      const r = await api.get(
        `/api/cdm/network-paths/blast-radius/${encodeURIComponent(blastAsset.trim())}`,
        { params: { max_hops: maxHops } },
      )
      setBlastNodes((r.data?.reachable || []) as BlastNode[])
    } catch (e) {
      setBlastError('Blast-radius walk failed — check the asset id')
      setBlastNodes([])
    } finally {
      setBlastLoading(false)
    }
  }

  const refreshNow = async () => {
    setRefreshing(true)
    setMessage('Re-feeding declared rules from F5 + rebuilding paths…')
    try {
      const r = await api.post('/api/cdm/network-paths/refresh')
      const p = r.data?.paths || {}
      setMessage(
        `Done. ${p.paths_total || 0} paths · ${p.confirmed || 0} confirmed · ${p.unused_permission || 0} unused · ${p.shadow_path || 0} shadow.`,
      )
      if (mode === 'paths') await loadPaths()
      if (mode === 'shadow') await loadShadow()
    } catch {
      setMessage('Refresh failed — admin permission required')
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    if (mode === 'paths') void loadPaths()
    else if (mode === 'shadow') void loadShadow()
  }, [mode, statusFilter, portFilter, protoFilter])

  const filteredPaths = useMemo(() => {
    if (!search) return paths
    const q = search.toLowerCase()
    return paths.filter(p =>
      p.src_asset_id.toLowerCase().includes(q) ||
      p.dst_asset_id.toLowerCase().includes(q),
    )
  }, [paths, search])

  const counts = useMemo(() => {
    const c: Record<string, number> = { confirmed: 0, unused_permission: 0, shadow_path: 0 }
    for (const p of paths) c[p.status] = (c[p.status] || 0) + 1
    return c
  }, [paths])

  const blastByHop = useMemo(() => {
    const groups: Record<number, BlastNode[]> = {}
    for (const n of blastNodes) {
      groups[n.shortest_hop] = groups[n.shortest_hop] || []
      groups[n.shortest_hop].push(n)
    }
    return Object.entries(groups)
      .map(([hop, nodes]) => ({ hop: Number(hop), nodes }))
      .sort((a, b) => a.hop - b.hop)
  }, [blastNodes])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Network size={18} className="text-emerald" /> CDM CONNECTIVITY
        </h1>
        <span className="text-[10px] text-text-muted">
          Declared × observed · blast-radius · whitespace vs Axonius
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={refreshNow}
            disabled={refreshing}
            className="btn-ghost inline-flex items-center gap-1 text-xs"
            title="Re-feed declared rules from F5 + rebuild cdm_network_paths"
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh paths'}
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-muted bg-bg-elev border border-border rounded px-2 py-1">
          {message}
        </div>
      )}

      {/* Mode tab bar */}
      <div className="flex gap-1 border-b border-border">
        {([
          { key: 'paths',  label: 'Paths',         icon: Network },
          { key: 'blast',  label: 'Blast radius',  icon: Crosshair },
          { key: 'shadow', label: 'Shadow paths',  icon: AlertTriangle },
        ] as Array<{ key: Mode; label: string; icon: typeof Network }>).map(t => {
          const Icon = t.icon
          const active = mode === t.key
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setMode(t.key)}
              className={clsx(
                'inline-flex items-center gap-1 px-3 py-2 text-xs border-b-2 -mb-[1px]',
                active
                  ? 'border-emerald text-text-primary'
                  : 'border-transparent text-text-muted hover:text-text-primary',
              )}
            >
              <Icon size={12} /> {t.label}
            </button>
          )
        })}
      </div>

      {mode === 'paths' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" size={12} />
              <input
                type="text"
                placeholder="Filter by asset id…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1 bg-bg-elev border border-border rounded text-xs"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as PathStatus | '')}
              className="bg-bg-elev border border-border rounded px-2 py-1 text-xs"
            >
              <option value="">All statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="unused_permission">Unused permission</option>
              <option value="shadow_path">Shadow</option>
            </select>
            <input
              type="number"
              placeholder="Port"
              value={portFilter}
              onChange={e => setPortFilter(e.target.value)}
              className="w-20 bg-bg-elev border border-border rounded px-2 py-1 text-xs"
            />
            <select
              value={protoFilter}
              onChange={e => setProtoFilter(e.target.value)}
              className="bg-bg-elev border border-border rounded px-2 py-1 text-xs"
            >
              <option value="">Any proto</option>
              <option value="tcp">TCP</option>
              <option value="udp">UDP</option>
              <option value="icmp">ICMP</option>
              <option value="any">any</option>
            </select>
            <span className="ml-auto text-[10px] text-text-muted">
              {filteredPaths.length} / {paths.length} ·
              <span className="text-emerald ml-1">{counts.confirmed} confirmed</span> ·
              <span className="text-blue-light ml-1">{counts.unused_permission} unused</span> ·
              <span className="text-rose-light ml-1">{counts.shadow_path} shadow</span>
            </span>
          </div>

          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-bg-elev text-text-muted">
                <tr>
                  <th className="text-left px-2 py-2 font-medium">SRC ASSET</th>
                  <th className="text-left px-2 py-2 font-medium">DST ASSET</th>
                  <th className="text-left px-2 py-2 font-medium">PORT</th>
                  <th className="text-left px-2 py-2 font-medium">PROTO</th>
                  <th className="text-right px-2 py-2 font-medium">RULES</th>
                  <th className="text-right px-2 py-2 font-medium">FLOWS</th>
                  <th className="text-left px-2 py-2 font-medium">LAST OBS</th>
                  <th className="text-left px-2 py-2 font-medium">STATUS</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="text-center py-4 text-text-muted">Loading…</td></tr>
                )}
                {!loading && filteredPaths.length === 0 && (
                  <tr><td colSpan={8} className="text-center py-4 text-text-muted">
                    No paths yet — refresh to feed declared rules and rebuild.
                  </td></tr>
                )}
                {filteredPaths.map(p => (
                  <tr key={p.id} className="border-t border-border hover:bg-bg-elev">
                    <td className="px-2 py-1 font-mono">{shortId(p.src_asset_id)}</td>
                    <td className="px-2 py-1 font-mono">{shortId(p.dst_asset_id)}</td>
                    <td className="px-2 py-1">{p.port || '—'}</td>
                    <td className="px-2 py-1">{p.proto}</td>
                    <td className="px-2 py-1 text-right">{p.allowed_by_count}</td>
                    <td className="px-2 py-1 text-right">{p.observed_in_count}</td>
                    <td className="px-2 py-1 text-text-muted">{formatTs(p.last_observed)}</td>
                    <td className="px-2 py-1">
                      <span className={clsx('inline-block px-1.5 py-0.5 text-[9px] rounded border', STATUS_TONE[p.status])}>
                        {STATUS_LABEL[p.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {mode === 'blast' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2 bg-bg-elev border border-border rounded p-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-text-muted block mb-1">
                Starting asset id
              </label>
              <input
                type="text"
                placeholder="UUID — paste or pick from /app/cdm/assets"
                value={blastAsset}
                onChange={e => setBlastAsset(e.target.value)}
                className="w-full bg-bg-base border border-border rounded px-2 py-1 text-xs font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-text-muted block mb-1">
                Max hops
              </label>
              <input
                type="number"
                min={1}
                max={10}
                value={maxHops}
                onChange={e => setMaxHops(Math.max(1, Math.min(10, Number(e.target.value) || 5)))}
                className="w-20 bg-bg-base border border-border rounded px-2 py-1 text-xs"
              />
            </div>
            <button
              type="button"
              onClick={runBlast}
              disabled={blastLoading || !blastAsset.trim()}
              className="btn-primary inline-flex items-center gap-1 text-xs"
            >
              <Play size={12} /> {blastLoading ? 'Walking…' : 'Walk reachability'}
            </button>
          </div>

          {blastError && (
            <div className="text-[11px] text-rose-light bg-rose/10 border border-rose/30 rounded px-2 py-1">
              {blastError}
            </div>
          )}

          {blastByHop.length === 0 && !blastLoading && (
            <div className="text-[11px] text-text-muted bg-bg-elev border border-border rounded px-3 py-4 text-center">
              No reachable assets yet. Pick a source asset, run the walk.
            </div>
          )}

          {blastByHop.map(group => (
            <div key={group.hop} className="border border-border rounded overflow-hidden">
              <div className="bg-bg-elev px-2 py-1 text-[10px] uppercase tracking-widest text-text-muted flex items-center gap-2">
                <Wifi size={11} /> Hop {group.hop} · {group.nodes.length} asset{group.nodes.length === 1 ? '' : 's'}
              </div>
              <table className="w-full text-xs">
                <thead className="text-text-muted">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">ASSET</th>
                    <th className="text-left px-2 py-1 font-medium">PATH</th>
                  </tr>
                </thead>
                <tbody>
                  {group.nodes.map(n => (
                    <tr key={n.asset_id} className="border-t border-border">
                      <td className="px-2 py-1 font-mono">{shortId(n.asset_id)}</td>
                      <td className="px-2 py-1 text-text-muted">
                        {n.via.map((e, i) => (
                          <span key={i} className="inline-flex items-center gap-1 mr-2">
                            <span className="font-mono">{shortId(e.src)}</span>
                            <span>→</span>
                            <span className="font-mono">{shortId(e.dst)}</span>
                            <span className="text-[9px] opacity-70">
                              {e.port}/{e.proto}
                            </span>
                            <span
                              className={clsx(
                                'inline-block px-1 py-0 text-[8px] rounded border ml-0.5',
                                STATUS_TONE[e.status],
                              )}
                            >
                              {STATUS_LABEL[e.status]}
                            </span>
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {mode === 'shadow' && (
        <div className="space-y-3">
          <div className="text-[11px] text-text-muted bg-rose/5 border border-rose/30 rounded px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={14} className="text-rose-light shrink-0 mt-0.5" />
            <div>
              Shadow paths are observed flows with NO matching declared rule.
              Each row is a finding — investigate why traffic flows that the
              firewall config doesn't authorise. Whitespace vs Axonius.
            </div>
          </div>

          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-bg-elev text-text-muted">
                <tr>
                  <th className="text-left px-2 py-2 font-medium">SRC ASSET</th>
                  <th className="text-left px-2 py-2 font-medium">DST ASSET</th>
                  <th className="text-left px-2 py-2 font-medium">PORT</th>
                  <th className="text-left px-2 py-2 font-medium">PROTO</th>
                  <th className="text-right px-2 py-2 font-medium">FLOWS</th>
                  <th className="text-left px-2 py-2 font-medium">LAST OBS</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="text-center py-4 text-text-muted">Loading…</td></tr>
                )}
                {!loading && shadowRows.length === 0 && (
                  <tr><td colSpan={6} className="text-center py-4 text-text-muted">
                    <Activity size={16} className="inline mr-1 text-emerald" />
                    No shadow paths — every observed flow has a matching declared rule. Green path.
                  </td></tr>
                )}
                {shadowRows.map(r => (
                  <tr key={r.id} className="border-t border-border hover:bg-bg-elev">
                    <td className="px-2 py-1 font-mono">{shortId(r.src_asset_id)}</td>
                    <td className="px-2 py-1 font-mono">{shortId(r.dst_asset_id)}</td>
                    <td className="px-2 py-1">{r.port || '—'}</td>
                    <td className="px-2 py-1">{r.proto}</td>
                    <td className="px-2 py-1 text-right">{r.observed_in_count}</td>
                    <td className="px-2 py-1 text-text-muted">{formatTs(r.last_observed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
